import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { BottomAppShowcase, PageShell } from '../public/PublicPrimitives';
import { CleroS, CleroWordmarkSvg } from '../Logo';
import {
  Clock,
  ArrowRight,
  ChevronRight,
  Share2,
  Check,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  FileQuestion,
} from 'lucide-react';
import { FaApple, FaAndroid } from 'react-icons/fa6';
import blogIndexData from '../../data/blogIndex.json';
import { ANDROID_INVITE_URL, IOS_INVITE_URL } from '../../constants/downloadLinks';

interface PostMeta {
  title: string;
  slug: string;
  excerpt: string;
  publishedAt: string;
  updatedAt: string;
  author: {
    name: string;
    slug: string;
  };
  coverImage: string;
  readingMinutes: number;
}

interface FullPost extends PostMeta {
  toc: { id: string; text: string; level: number }[];
  html: string;
  wordCount: number;
}

const postsMeta: PostMeta[] = blogIndexData as PostMeta[];

// Lazy loader for individual post full payloads
const postModules = import.meta.glob('../../data/posts/*.json');
const postCache = new Map<string, FullPost>();

interface BlogPageProps {
  currentPath: string;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const INITIAL_VISIBLE_COUNT = 12;
const PAGE_INCREMENT = 12;

// High-Performance Picture Component supporting AVIF & WebP
const BlogPicture: React.FC<{
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
}> = memo(({ src, alt, className = '', loading = 'lazy' }) => {
  const isLocal = src.startsWith('/') && !src.startsWith('//');
  if (!isLocal) {
    return <img src={src} alt={alt} className={className} loading={loading} decoding="async" />;
  }
  const base = src.replace(/\.(png|jpg|jpeg)$/i, '');
  return (
    <picture>
      <source srcSet={`${base}.avif`} type="image/avif" />
      <source srcSet={`${base}.webp`} type="image/webp" />
      <img src={src} alt={alt} className={className} loading={loading} decoding="async" />
    </picture>
  );
});
BlogPicture.displayName = 'BlogPicture';

// Memoized Article Card Component (Zero unnecessary re-renders)
const ArticleCard = memo(({ post, idx, onSelect }: { post: PostMeta; idx: number; onSelect: (slug: string) => void }) => {
  return (
    <article
      onClick={() => onSelect(post.slug)}
      className="group cursor-pointer flex flex-col justify-between rounded-3xl border border-slate-200/80 bg-white p-5 shadow-xs hover:shadow-md transition hover:-translate-y-1 space-y-4"
      style={{ contentVisibility: 'auto', containIntrinsicSize: '0 380px' }}
    >
      <div className="space-y-3">
        <div className="aspect-[16/10] overflow-hidden rounded-2xl bg-slate-100 relative">
          <BlogPicture
            src={post.coverImage}
            alt={post.title}
            loading={idx < 4 ? 'eager' : 'lazy'}
            className="h-full w-full object-cover transition group-hover:scale-105"
          />
        </div>

        <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
          <span>{formatDate(post.publishedAt)}</span>
          <span>·</span>
          <span>{post.readingMinutes} min read</span>
        </div>

        <h2 className="text-base sm:text-lg font-bold text-slate-950 leading-snug group-hover:text-teal-700 transition line-clamp-2">
          {post.title}
        </h2>

        <p className="text-xs sm:text-sm text-slate-600 font-medium line-clamp-2 leading-relaxed">
          {post.excerpt}
        </p>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <span className="text-[11px] font-semibold text-slate-400">{post.author.name}</span>
        <div className="flex items-center gap-1 text-xs font-bold text-teal-700 group-hover:translate-x-1 transition-transform">
          <span>Read article</span>
          <ArrowRight size={13} />
        </div>
      </div>
    </article>
  );
});
ArticleCard.displayName = 'ArticleCard';

export const BlogPage: React.FC<BlogPageProps> = ({ currentPath }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState<string>('');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [copiedLink, setCopiedLink] = useState(false);
  const [activePost, setActivePost] = useState<FullPost | null>(null);
  const [isLoadingPost, setIsLoadingPost] = useState(false);
  const [postLoadError, setPostLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  const progressBarRef = useRef<HTMLDivElement>(null);
  const isScrolledRef = useRef(false);

  const normalizedPath = currentPath.split('?')[0].replace(/\/$/, '');
  const activeSlug = useMemo(() => {
    if (!normalizedPath.startsWith('/blog/')) return null;
    const parts = normalizedPath.replace('/blog/', '').split('/');
    if (parts.length === 1 && !['category', 'tag', 'author'].includes(parts[0])) {
      return parts[0];
    }
    return null;
  }, [normalizedPath]);

  // Load single post payload with in-memory caching
  useEffect(() => {
    if (!activeSlug) {
      setIsLoadingPost(false);
      setActivePost(null);
      setPostLoadError(false);
      return;
    }

    if (postCache.has(activeSlug)) {
      setIsLoadingPost(false);
      setActivePost(postCache.get(activeSlug)!);
      setPostLoadError(false);
      return;
    }

    const matchedMeta = postsMeta.find((p) => p.slug === activeSlug);
    const moduleKey = `../../data/posts/${activeSlug}.json`;
    const loader = postModules[moduleKey];

    let isCancelled = false;

    if (loader) {
      setIsLoadingPost(true);
      setPostLoadError(false);
      loader()
        .then((mod: any) => {
          if (isCancelled) return;
          const postData = mod.default || mod;
          postCache.set(activeSlug, postData);
          setActivePost(postData);
          setIsLoadingPost(false);
          setPostLoadError(false);
        })
        .catch(() => {
          if (isCancelled) return;
          setIsLoadingPost(false);
          setPostLoadError(true);
        });
      return () => {
        isCancelled = true;
      };
    } else if (matchedMeta) {
      const fallback: FullPost = {
        ...matchedMeta,
        toc: [],
        html: `<p>${matchedMeta.excerpt}</p>`,
        wordCount: 100,
      };
      postCache.set(activeSlug, fallback);
      setIsLoadingPost(false);
      setActivePost(fallback);
      setPostLoadError(false);
    } else {
      setIsLoadingPost(false);
      setActivePost(null);
      setPostLoadError(false);
    }
  }, [activeSlug, retryCount]);

  // High-performance native scroll handler (Zero React state re-renders during smooth scroll)
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          const scrolled = scrollY > 30;

          if (scrolled !== isScrolledRef.current) {
            isScrolledRef.current = scrolled;
            setIsScrolled(scrolled);
          }

          // Direct DOM progress bar update (0 React re-renders)
          if (progressBarRef.current) {
            const winHeight = window.innerHeight;
            const docHeight = document.documentElement.scrollHeight;
            const totalScroll = docHeight - winHeight;
            if (totalScroll > 0) {
              const progress = Math.min(100, Math.max(0, (scrollY / totalScroll) * 100));
              progressBarRef.current.style.width = `${progress}%`;
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // IntersectionObserver for Table of Contents (Zero layout thrashing)
  useEffect(() => {
    if (!activePost || !activePost.toc || activePost.toc.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeadingId(entry.target.id);
          }
        });
      },
      { rootMargin: '-70px 0px -70% 0px' }
    );

    const headings = document.querySelectorAll<HTMLElement>('article h2, article h3');
    headings.forEach((h) => observer.observe(h));

    return () => observer.disconnect();
  }, [activePost]);

  const navigate = useCallback((path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSelectPost = useCallback((slug: string) => {
    navigate(`/blog/${slug}`);
  }, [navigate]);

  const featuredPost = postsMeta[0] || null;
  const gridPosts = useMemo(() => {
    return postsMeta.slice(1, visibleCount + 1);
  }, [visibleCount]);

  const hasMore = visibleCount + 1 < postsMeta.length;

  const relatedPosts = useMemo(() => {
    if (!activeSlug) return [];
    return postsMeta
      .filter((p) => p.slug !== activeSlug)
      .slice(0, 3);
  }, [activeSlug]);

  const handleShare = useCallback(() => {
    if (navigator.share) {
      navigator.share({
        title: activePost?.title || 'Clero Blog',
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  }, [activePost?.title]);

  // Dynamic SEO Meta Tags Update for Client-Side SPA Navigation
  useEffect(() => {
    const title = activePost ? `${activePost.title} | Clero` : 'Clero Blog | Group Payments & Shared Expenses';
    const description = activePost
      ? activePost.excerpt
      : 'Articles on splitting costs, group requests, and shared purchases without debt friction.';
    const url = activeSlug ? `https://clero.money/blog/${activeSlug}` : 'https://clero.money/blog';
    const image = activePost?.coverImage
      ? (activePost.coverImage.startsWith('http') ? activePost.coverImage : `https://clero.money${activePost.coverImage}`)
      : 'https://clero.money/og-image.png';

    document.title = title;

    // Update Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    // Update Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Update OpenGraph
    const setOg = (property: string, content: string) => {
      let og = document.querySelector(`meta[property="${property}"]`);
      if (!og) {
        og = document.createElement('meta');
        og.setAttribute('property', property);
        document.head.appendChild(og);
      }
      og.setAttribute('content', content);
    };

    setOg('og:title', title);
    setOg('og:description', description);
    setOg('og:url', url);
    setOg('og:image', image);
    setOg('og:type', activePost ? 'article' : 'website');

    // JSON-LD Structured Data
    const scriptId = 'blog-json-ld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    if (activePost) {
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: activePost.title,
        description: activePost.excerpt,
        image: [image],
        datePublished: activePost.publishedAt,
        dateModified: activePost.updatedAt || activePost.publishedAt,
        author: {
          '@type': 'Organization',
          name: activePost.author.name,
          url: 'https://clero.money',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Clero',
          url: 'https://clero.money',
          logo: {
            '@type': 'ImageObject',
            url: 'https://clero.money/clero_icon.png',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': url,
        },
      });
    } else {
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Clero Blog',
        description: description,
        url: url,
        publisher: {
          '@type': 'Organization',
          name: 'Clero',
          url: 'https://clero.money',
        },
      });
    }
  }, [activePost, activeSlug]);

  return (
    <PageShell
      background="bg-[#fafaf7]"
      description={activePost ? activePost.excerpt : "Articles on splitting costs, group requests, and shared purchases without debt friction."}
      title={activePost ? `${activePost.title} | Clero Blog` : "Clero Blog | Group Payments & Shared Expenses"}
    >
      {/* READING PROGRESS BAR (Direct DOM transformed, 0% React overhead) */}
      {activeSlug && (
        <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-transparent pointer-events-none">
          <div
            ref={progressBarRef}
            className="h-full bg-[var(--clero-primary)] transition-all duration-75 ease-out"
            style={{ width: '0%' }}
          />
        </div>
      )}

      {/* FLOATING HEADER (FLIGHTY MORPHING STYLE) */}
      <div
        className={`fixed top-0 left-0 right-0 z-40 flex justify-center pointer-events-none transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isScrolled ? 'pt-3.5 px-4 sm:px-6' : 'pt-0 px-0'
        }`}
      >
        <header
          className={`pointer-events-auto w-full transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isScrolled
              ? 'max-w-[480px] rounded-[999px] bg-white/90 backdrop-blur-2xl border border-slate-200/80 px-4 sm:px-5 py-2 shadow-sm'
              : 'max-w-[720px] sm:max-w-[760px] rounded-[0px] bg-transparent backdrop-blur-none border-b border-slate-200/50 px-4 sm:px-6 py-3.5'
          }`}
        >
          <div className="flex items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-2 shrink-0">
              <a
                aria-label="Clero Home"
                className="flex items-center transition opacity-95 hover:opacity-100"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
              >
                <CleroWordmarkSvg height={24} color="#0D9488" />
              </a>
            </div>

            <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
              <button
                className="text-xs sm:text-sm font-bold text-slate-700 hover:text-slate-950 transition-colors whitespace-nowrap px-1 py-1"
                onClick={() => navigate('/support')}
                type="button"
              >
                Help Center
              </button>

              <button
                className="inline-flex items-center justify-center rounded-full bg-[var(--clero-primary)] px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold text-white transition-all hover:bg-[var(--clero-primary-dark)] shadow-sm active:scale-95 whitespace-nowrap"
                onClick={() => {
                  const el = document.getElementById('bottom-download');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                type="button"
              >
                Download App
              </button>
            </div>
          </div>
        </header>
      </div>

      <div className="h-16 sm:h-20" />

      {/* MAIN CONTAINER */}
      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {activeSlug ? (
          /* ========================================================================= */
          /* ---------------------- SINGLE ARTICLE EDITORIAL VIEW -------------------- */
          /* ========================================================================= */
          <article className="space-y-10">
            {/* Top Breadcrumb Trail */}
            <nav className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-500 flex-wrap">
              <button onClick={() => navigate('/')} className="hover:text-slate-950 transition">Home</button>
              <ChevronRight size={14} className="text-slate-400" />
              <button onClick={() => navigate('/blog')} className="hover:text-slate-950 transition">Blog</button>
              <ChevronRight size={14} className="text-slate-400" />
              <span className="text-slate-800 font-medium line-clamp-1 max-w-[280px] sm:max-w-md">
                {activePost?.title || (postLoadError ? 'Error loading article' : 'Article')}
              </span>
            </nav>

            {isLoadingPost && !activePost ? (
              <div className="py-24 text-center space-y-3">
                <div className="h-8 w-8 rounded-full border-2 border-teal-600 border-t-transparent animate-spin mx-auto" />
                <p className="text-xs font-semibold text-slate-400">Loading article...</p>
              </div>
            ) : postLoadError ? (
              <div className="py-24 text-center space-y-4 max-w-md mx-auto">
                <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
                  <AlertCircle size={24} />
                </div>
                <h2 className="text-2xl font-black text-slate-950">This article could not be loaded</h2>
                <p className="text-sm font-medium text-slate-500">
                  We encountered an error loading this post. Please try again or return to the blog.
                </p>
                <div className="flex items-center justify-center gap-3 pt-2">
                  <button
                    onClick={() => setRetryCount((c) => c + 1)}
                    className="px-5 py-2.5 bg-teal-600 text-white rounded-xl text-xs font-bold hover:bg-teal-700 transition"
                  >
                    Try again
                  </button>
                  <button
                    onClick={() => navigate('/blog')}
                    className="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-200 transition"
                  >
                    Back to blog
                  </button>
                </div>
              </div>
            ) : !activePost ? (
              <div className="py-24 text-center space-y-4 max-w-md mx-auto">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-600 flex items-center justify-center mx-auto">
                  <FileQuestion size={24} />
                </div>
                <h2 className="text-2xl font-black text-slate-950">Article not found</h2>
                <p className="text-sm font-medium text-slate-500">
                  The article you're looking for doesn't exist or has been moved.
                </p>
                <div className="pt-2">
                  <button
                    onClick={() => navigate('/blog')}
                    className="px-5 py-2.5 bg-teal-600 text-white rounded-xl text-xs font-bold hover:bg-teal-700 transition"
                  >
                    Back to blog
                  </button>
                </div>
              </div>
            ) : activePost ? (
              <>
                {/* Article Header */}
                <header className="space-y-4 max-w-4xl">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <Clock size={13} />
                    <span>{activePost.readingMinutes} min read</span>
                    <span>•</span>
                    <span>{formatDate(activePost.publishedAt)}</span>
                  </div>

                  <h1 className="text-3xl sm:text-4xl lg:text-[3rem] font-black text-slate-950 tracking-tight leading-[1.14]">
                    {activePost.title}
                  </h1>

                  <p className="text-base sm:text-xl text-slate-600 font-normal leading-relaxed">
                    {activePost.excerpt}
                  </p>

                  {/* Author & Timestamp Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-slate-200/80">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                        {activePost.author.name[0]}
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-bold text-slate-900">{activePost.author.name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-400 font-medium">
                          <span>Published {formatDate(activePost.publishedAt)}</span>
                          {activePost.updatedAt && activePost.updatedAt !== activePost.publishedAt && (
                            <>
                              <span>•</span>
                              <span>Updated {formatDate(activePost.updatedAt)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={handleShare}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/90 bg-white px-4 py-2 text-xs font-bold text-slate-700 shadow-2xs hover:bg-slate-50 transition active:scale-95"
                    >
                      {copiedLink ? <Check size={14} className="text-teal-600" /> : <Share2 size={14} />}
                      <span>{copiedLink ? 'Link Copied' : 'Share Article'}</span>
                    </button>
                  </div>
                </header>

                {/* Two-Column Reading Grid (Desktop 8 col body + 4 col sticky sidebar) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start pt-2">
                  
                  {/* Main Reading Column */}
                  <div className="lg:col-span-8 space-y-8 min-w-0">
                    <div
                      className="blog-article-content"
                      dangerouslySetInnerHTML={{ __html: activePost.html }}
                    />

                    {/* Editorial Standard Transparency Card (E-E-A-T) */}
                    <div className="rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-7 shadow-xs space-y-3">
                      <div className="flex items-center gap-2 text-slate-900 font-bold text-sm">
                        <ShieldCheck size={18} className="text-teal-600" />
                        <span>Editorial Standards & Verification</span>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium">
                        This article was researched and written by the Clero team based on real product settlement workflows and US bank payment rails. We review all published articles when product features or payment regulations update.
                      </p>
                    </div>
                  </div>

                  {/* Sticky Sidebar Companion (Desktop Only) */}
                  <aside className="hidden lg:block lg:col-span-4 sticky top-28 space-y-6">
                    
                    {/* Table of Contents */}
                    {activePost.toc && activePost.toc.length > 0 && (
                      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-xs space-y-3">
                        <p className="text-xs font-extrabold uppercase tracking-wider text-slate-900">
                          Table of Contents
                        </p>
                        <nav className="space-y-1 text-xs font-semibold text-slate-600 max-h-[440px] overflow-y-auto pr-1">
                          {activePost.toc.map((heading) => {
                            const isActive = activeHeadingId === heading.id;
                            return (
                              <a
                                key={heading.id}
                                href={`#${heading.id}`}
                                className={`block py-1.5 px-3 rounded-xl transition-all ${
                                  heading.level === 3 ? 'pl-6 text-slate-500 font-normal' : ''
                                } ${
                                  isActive
                                    ? 'bg-teal-50 text-teal-800 font-bold translate-x-1'
                                    : 'hover:bg-slate-50 hover:text-slate-900'
                                }`}
                              >
                                {heading.text}
                              </a>
                            );
                          })}
                        </nav>
                      </div>
                    )}

                  </aside>

                </div>

                {/* Related Articles Section */}
                {relatedPosts.length > 0 && (
                  <section className="space-y-6 pt-12 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-black text-slate-950 tracking-tight">Related Articles</h3>
                      <button
                        onClick={() => navigate('/blog')}
                        className="inline-flex items-center gap-1 text-xs font-bold text-teal-700 hover:text-teal-900 transition"
                      >
                        <span>View all articles</span>
                        <ArrowRight size={14} />
                      </button>
                    </div>

                    <div className="grid gap-6 sm:grid-cols-3">
                      {relatedPosts.map((post) => (
                        <article
                          key={post.slug}
                          onClick={() => navigate(`/blog/${post.slug}`)}
                          className="group cursor-pointer flex flex-col justify-between rounded-3xl border border-slate-200/80 bg-white p-5 shadow-xs hover:shadow-md transition hover:-translate-y-1 space-y-4"
                        >
                          <div className="space-y-3">
                            <div className="aspect-[16/10] overflow-hidden rounded-2xl bg-slate-100">
                              <BlogPicture
                                src={post.coverImage}
                                alt={post.title}
                                className="h-full w-full object-cover transition group-hover:scale-105"
                                loading="lazy"
                              />
                            </div>
                            <p className="text-xs font-semibold text-slate-400">{formatDate(post.publishedAt)}</p>
                            <h4 className="text-base font-bold text-slate-950 line-clamp-2 group-hover:text-teal-700 transition leading-snug">
                              {post.title}
                            </h4>
                            <p className="text-xs text-slate-600 font-medium line-clamp-2 leading-relaxed">
                              {post.excerpt}
                            </p>
                          </div>

                          <div className="flex items-center gap-1 text-xs font-bold text-teal-700 pt-1 group-hover:translate-x-1 transition-transform">
                            <span>Read article</span>
                            <ArrowRight size={13} />
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                )}
              </>
            ) : null}
          </article>
        ) : (
          /* ========================================================================= */
          /* ----------------------- EDITORIAL BLOG HUB VIEW ------------------------- */
          /* ========================================================================= */
          <div className="space-y-12">
            
            {/* Header */}
            <div className="space-y-3 text-center max-w-3xl mx-auto">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-950 tracking-tight">
                Group Payments & Shared Purchases
              </h1>
              <p className="text-base sm:text-lg text-slate-600 font-medium leading-relaxed max-w-2xl mx-auto">
                Practical breakdowns, comparison playbooks, and articles on moving from shared expense to fully settled with zero debt friction.
              </p>
            </div>

            {/* Featured Lead Story */}
            {featuredPost && (
              <div
                onClick={() => handleSelectPost(featuredPost.slug)}
                className="group cursor-pointer rounded-3xl sm:rounded-[36px] border border-slate-200/80 bg-white p-6 sm:p-8 shadow-xs hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-7 space-y-4">
                    <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
                      <span>{featuredPost.readingMinutes} min read</span>
                      <span>•</span>
                      <span>{formatDate(featuredPost.publishedAt)}</span>
                    </div>

                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-950 tracking-tight leading-tight group-hover:text-teal-700 transition">
                      {featuredPost.title}
                    </h2>

                    <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed line-clamp-3">
                      {featuredPost.excerpt}
                    </p>

                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-xs">
                          {featuredPost.author.name[0]}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-900">{featuredPost.author.name}</p>
                          <p className="text-[11px] text-slate-400 font-medium">{formatDate(featuredPost.publishedAt)}</p>
                        </div>
                      </div>

                      <div className="inline-flex items-center gap-1.5 text-xs font-bold text-teal-700 group-hover:translate-x-1 transition-transform">
                        <span>Read full article</span>
                        <ArrowRight size={14} />
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-5 aspect-[16/11] overflow-hidden rounded-2xl sm:rounded-3xl bg-slate-100">
                    <BlogPicture
                      src={featuredPost.coverImage}
                      alt={featuredPost.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      loading="eager"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Editorial Articles Grid (Fast, Paginated on-demand) */}
            <div className="space-y-8">
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {gridPosts.map((post, idx) => (
                  <ArticleCard
                    key={post.slug}
                    post={post}
                    idx={idx}
                    onSelect={handleSelectPost}
                  />
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center pt-4">
                  <button
                    onClick={() => setVisibleCount((prev) => prev + PAGE_INCREMENT)}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200/90 bg-white px-8 py-3 text-xs sm:text-sm font-bold text-slate-900 shadow-2xs hover:bg-slate-50 transition active:scale-95"
                  >
                    <span>Load More Articles ({postsMeta.length - visibleCount - 1} remaining)</span>
                  </button>
                </div>
              )}
            </div>

          </div>
        )}
      </main>

      {/* SHOWCASE SECTION */}
      <div className="mt-12 mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8 mb-12">
        <BottomAppShowcase />
      </div>

      {/* FOOTER */}
      <footer className="border-t border-slate-200/80 bg-white px-4 py-12 sm:px-6 lg:px-8 mt-16">
        <div className="mx-auto max-w-[var(--clero-max-content)]">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-4 sm:col-span-2 lg:col-span-1">
              <img src="/clero_icon.png" alt="Clero" className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl shadow-sm object-cover" />
              <p className="text-xs font-semibold text-slate-400">
                Secured with direct bank connections via Plaid & Astra Financial.
              </p>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Compare</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-teal-700" onClick={() => navigate('/compare')} type="button">All Splitting Apps</button></li>
                <li><button className="transition hover:text-teal-700" onClick={() => navigate('/compare/splitwise')} type="button">Clero vs Splitwise</button></li>
                <li><button className="transition hover:text-teal-700" onClick={() => navigate('/compare/venmo')} type="button">Clero vs Venmo</button></li>
                <li><button className="transition hover:text-teal-700" onClick={() => navigate('/compare/zelle')} type="button">Clero vs Zelle</button></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Product</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/')} type="button">Features</button></li>
                <li><a className="transition hover:text-slate-950" href={IOS_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for iOS</a></li>
                <li><a className="transition hover:text-slate-950" href={ANDROID_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for Android</a></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Resources</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="text-teal-700 font-bold" onClick={() => navigate('/blog')} type="button">Blog</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/support')} type="button">Help & Support</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/compare')} type="button">Compare Apps</button></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Legal & Privacy</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/privacy')} type="button">Privacy Policy</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/terms')} type="button">Terms of Service</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/data-deletion')} type="button">Data Deletion</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 border-t border-slate-100 pt-6 text-center text-xs text-slate-400">
            <p>© 2026 Oryn Technologies Inc. All rights reserved. Clero is a financial technology platform, not a bank.</p>
          </div>
        </div>
      </footer>
    </PageShell>
  );
};

export default BlogPage;
